import {
  Button,
  ConfirmModal,
  EditPencil,
  HandlerIcon,
  LinkBehavior,
} from '@/components';
import { CollapseV2 } from '@/components/Collapse/CollapseV2';
import { DeleteButton } from '@/components/DeleteButton/DeleteButton';
import { RoutePath } from '@/core/router';
import { ProcessDetailsWorkflow } from '@/modules/Processes/domain';
import { EditWorkflowModal } from '@/modules/Workflows/components/EditWorkflowModal';
import { WorkflowTaskModal } from '@/modules/Workflows/pages/WorkflowDetailsPage/components';
import { useDeleteWorkflow } from '@/modules/Workflows/queries';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MouseEvent, PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

type WorkflowCollapsableProps = PropsWithChildren<{
  workflow: ProcessDetailsWorkflow;
  businessAreaId: number;
  disableDrag?: boolean;
}>;

export const DraggableWorkflowCollapse = ({
  workflow,
  businessAreaId,
  children,
  disableDrag,
}: WorkflowCollapsableProps) => {
  const { t } = useTranslation();
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [workflowSelected, setWorkflowSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id: `w_${workflow.id}`,
      data: workflow,
      disabled: disableDrag,
    });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const { mutate: deleteWorkflow, isLoading: isDeletingWorkflow } =
    useDeleteWorkflow();

  const handleOpenRemoveWorkflowModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setWorkflowSelected(id);
  }, []);

  const handleCloseRemoveWorkflowModalState = useCallback(() => {
    setIsOpenDelete(false);
    setWorkflowSelected(undefined);
  }, []);

  const handleDeleteWorkflow = useCallback(() => {
    if (!workflowSelected) {
      return;
    }

    deleteWorkflow(workflowSelected);

    handleCloseRemoveWorkflowModalState();
  }, [workflowSelected, deleteWorkflow, handleCloseRemoveWorkflowModalState]);

  return (
    <>
      <div className="flex flex-col" ref={setNodeRef} style={style}>
        <CollapseV2
          title={
            <LinkBehavior
              className="hover:underline"
              href={RoutePath.workflowDetail(workflow.id.toString())}
            >
              {workflow.name}
            </LinkBehavior>
          }
          description={`(${workflow.tasks.length} Tasks)`}
          actions={
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCreateTaskModalOpen(true);
                }}
                variant="text"
                className="h-full py-0 font-bold text-blueNuit underline"
              >
                + {t('add_task')}
              </Button>
              <button
                className="flex size-4 h-full cursor-pointer items-center justify-center"
                {...attributes}
                {...listeners}
              >
                <HandlerIcon />
              </button>
              <Button
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setIsEditModalOpen(true);
                }}
                variant="text"
                className="size-4 h-full font-bold text-blueNuit underline"
              >
                <EditPencil className="text-gray-500 opacity-50" />
              </Button>
              <DeleteButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenRemoveWorkflowModalState(workflow.id);
                }}
                disabled={!workflow.canDelete || isDeletingWorkflow}
                tooltip={!isDeletingWorkflow && t('delete_workflow')}
                className={twMerge('', !workflow.canDelete && 'opacity-30')}
              />
            </>
          }
        >
          {children}
        </CollapseV2>
      </div>
      {isCreateTaskModalOpen && (
        <WorkflowTaskModal
          id={workflow.id}
          isOpen={isCreateTaskModalOpen}
          businessAreaId={businessAreaId}
          handleClose={() => setIsCreateTaskModalOpen(false)}
        />
      )}
      {isEditModalOpen && (
        <EditWorkflowModal
          isOpen={isEditModalOpen}
          id={workflow.id}
          handleClose={() => setIsEditModalOpen(false)}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveWorkflowModalState}
        title={t('please_confirm_action')}
        message={t('confirm_workflow_delete')}
        onConfirm={handleDeleteWorkflow}
      />
    </>
  );
};
